import { useEffect, useMemo, useRef, useState } from "react";
import { GlBasemap } from "../lib/basemap";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "../lib/theme";
import { renderToStaticMarkup } from "react-dom/server";

function pinIcon() {
  const html = renderToStaticMarkup(
    <div className="truck-pin">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 6h11v10H3zM14 9h4l3 3v4h-7z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" fill="none" />
        <circle cx="7" cy="17" r="2" fill="white" />
        <circle cx="17" cy="17" r="2" fill="white" />
      </svg>
    </div>,
  );
  return L.divIcon({ html, className: "truck-marker", iconSize: [34, 34], iconAnchor: [17, 34] });
}

function Tiles() {
  const { theme } = useTheme();
  return <GlBasemap dark={theme === "dark"} key={theme} />;
}

function RecenterTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.setView([target.lat, target.lng], 15, { animate: true });
  }, [target, map]);
  return null;
}

/**
 * Draggable-pin mini map.
 * Props:
 *  - lat, lng (numbers)
 *  - onChange({lat, lng})
 */
export default function LocationPicker({ lat, lng, onChange, height = 180 }) {
  const icon = useMemo(() => pinIcon(), []);
  const markerRef = useRef(null);
  const [local, setLocal] = useState({ lat, lng });

  // Sync incoming props (e.g. after geocoding) into local state
  useEffect(() => {
    if (lat != null && lng != null) setLocal({ lat, lng });
  }, [lat, lng]);

  if (local.lat == null || local.lng == null) return null;

  return (
    <div
      className="rounded-xl overflow-hidden border border-border"
      style={{ height }}
      data-testid="location-picker"
    >
      <MapContainer
        center={[local.lat, local.lng]}
        zoom={15}
        scrollWheelZoom={false}
        className="h-full w-full"
      >
        <Tiles />
        <RecenterTo target={{ lat: local.lat, lng: local.lng }} />
        <Marker
          position={[local.lat, local.lng]}
          icon={icon}
          draggable
          ref={markerRef}
          eventHandlers={{
            dragend: () => {
              const m = markerRef.current;
              if (!m) return;
              const p = m.getLatLng();
              const next = { lat: p.lat, lng: p.lng };
              setLocal(next);
              onChange?.(next);
            },
          }}
        />
      </MapContainer>
    </div>
  );
}
